/**
 * File: PeerReviewRecommendationBox.tsx
 * Description: Presents Archie's synthesized recommendation for a single
 *   checklist item - a concise summary plus aligned arguments, unresolved
 *   arguments and proposed options to resolve them.
 * Author: GitHub Copilot
 */
import {
  ArrowPathIcon,
  ArrowRightCircleIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import {
  PeerReviewCitation,
  PeerReviewRecommendation,
  PeerReviewRecommendationEntry,
} from '../../types/review';

const RECOMMENDATION_LOADING_MESSAGE =
  'Archie is synthesising a recommendation…';
const RECOMMENDATION_EMPTY_MESSAGE =
  'No recommendation yet. Ask Archie a question to refine the debate.';

interface RecommendationGroupMeta {
  title: string;
  Icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  chip: string;
}

const CITATIONS_LABEL = 'Source cite';
const VIEW_CITATIONS_LABEL = 'view';

interface CitationGroup {
  documentTitle: string;
  rules: string[];
}

const getCitationGroups = (citations: PeerReviewCitation[]): CitationGroup[] => {
  const groupedCitations = new Map<string, Set<string>>();

  citations.forEach((citation) => {
    const existingRules = groupedCitations.get(citation.document_title) ?? new Set<string>();
    existingRules.add(citation.principle_or_rule);
    groupedCitations.set(citation.document_title, existingRules);
  });

  return Array.from(groupedCitations.entries()).map(([documentTitle, rules]) => ({
    documentTitle,
    rules: Array.from(rules),
  }));
};

const OPTIONS_META: RecommendationGroupMeta = {
  title: 'Options to resolve',
  Icon: ArrowRightCircleIcon,
  iconColor: 'text-indigo-600',
  chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

/**
 * Render the exact guidance citations supporting a point.
 */
const CitationList: React.FC<{
  citations: PeerReviewCitation[];
  compact?: boolean;
}> = ({ citations, compact = false }) => {
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const citationGroups = getCitationGroups(citations);

  if (citationGroups.length === 0) {
    return null;
  }

  const toggleSource = (source: string) => {
    setExpandedSources((previousSources) => {
      const nextSources = new Set(previousSources);
      if (nextSources.has(source)) {
        nextSources.delete(source);
      } else {
        nextSources.add(source);
      }
      return nextSources;
    });
  };

  return (
    <div className={`flex flex-col gap-1 ${compact ? 'mt-1' : 'mt-2'}`}>
      {!compact && (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {CITATIONS_LABEL}
        </p>
      )}
      {citationGroups.map((citationGroup, index) => {
        const isExpanded = expandedSources.has(citationGroup.documentTitle);

        return (
          <div
            key={`${citationGroup.documentTitle}-${index}`}
            className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold text-slate-700">
                {citationGroup.documentTitle}
              </p>
              <button
                type="button"
                onClick={() => toggleSource(citationGroup.documentTitle)}
                className="text-[11px] font-medium text-[#336094] underline underline-offset-2 hover:text-[#6e94c0]"
              >
                {VIEW_CITATIONS_LABEL}
              </button>
            </div>
            {isExpanded && (
              <ul className="mt-1 list-disc space-y-1 pl-4 text-[11px] leading-snug text-slate-600">
                {citationGroup.rules.map((rule) => (
                  <li key={`${citationGroup.documentTitle}-${rule}`}>{rule}</li>
                ))}
              </ul>
            )}
          </div>
        );})}
    </div>
  );
};

/**
 * Render one labelled group as a list.
 */
const RecommendationGroup: React.FC<{
  meta: RecommendationGroupMeta;
  entries: PeerReviewRecommendationEntry[];
}> = ({ meta, entries }) => {
  if (entries.length === 0) {
    return null;
  }
  const { Icon } = meta;
  return (
    <div className="flex flex-col gap-1.5">
      <span
        className={
          'inline-flex w-fit items-center gap-1 rounded-full border px-2 ' +
          `py-0.5 text-[11px] font-semibold uppercase tracking-wide ${meta.chip}`
        }
      >
        <Icon className={`h-3.5 w-3.5 ${meta.iconColor}`} />
        {meta.title}
      </span>
      <ul className="flex flex-col gap-1 pl-1">
        {entries.map((entry, index) => (
          <li
            key={`${meta.title}-${index}`}
            className="flex flex-col gap-1 text-xs leading-snug text-gray-700"
          >
            <div className="flex items-start gap-1.5">
              <Icon
                className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${meta.iconColor}`}
              />
              <span>{entry.text}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

interface PeerReviewRecommendationBoxProps {
  recommendation?: PeerReviewRecommendation | null;
  isLoading: boolean;
}

/**
 * "Archie's recommendation" card shown inside an expanded checklist row.
 */
export const PeerReviewRecommendationBox: React.FC<
  PeerReviewRecommendationBoxProps
> = ({ recommendation, isLoading }) => {
  const hasContent =
    recommendation &&
    (recommendation.summary.length > 0 ||
      recommendation.options.length > 0);

  return (
    <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-white p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600 text-white">
          <LightBulbIcon className="h-4 w-4" />
        </span>
        <h4 className="text-sm font-bold text-indigo-900">
          Archie&apos;s recommendation
        </h4>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-indigo-700">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          {RECOMMENDATION_LOADING_MESSAGE}
        </div>
      )}

      {!isLoading && !hasContent && (
        <p className="text-xs text-gray-500">{RECOMMENDATION_EMPTY_MESSAGE}</p>
      )}

      {!isLoading && hasContent && recommendation && (
        <div className="flex flex-col gap-3">
          {recommendation.summary && (
            <div>
              <p className="text-sm leading-snug text-gray-800">
                {recommendation.summary}
              </p>
              <CitationList citations={recommendation.summary_citations} />
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-1">
            <RecommendationGroup
              meta={OPTIONS_META}
              entries={recommendation.options}
            />
          </div>
        </div>
      )}
    </div>
  );
};
